import { useMemo } from "react";
import { Eye, RefreshCw } from "lucide-react";

import { Button } from "components/ui/Button";
import { DataTable } from "components/ui/DataTable";
import { Notice, NoticeText } from "components/ui/Notice";
import { Pill, PILL_COLORS } from "components/ui/Pill";
import { toTitleCase } from "utils/text";

import styles from "../../HealthlinkDetailsPage.module.css";

// A category is a taxonomy, not a status, so these carry no judgement — the colours are
// only there to let the eye group the rows. Same treatment document types already get.
//
// An unmapped category still renders, in grey: Healthlink adds tables of its own, and one
// arriving unlabelled is better than one arriving invisible. Six exist on UAT today —
// GENERAL, BOOLEAN, SYMPTOMS, LANGUAGE, TITLES, DEMOGRAPHICS — but the list is theirs, not
// ours, so it is not treated as closed.
const CATEGORY_COLORS = {
  GENERAL: PILL_COLORS.docClosed,
  BOOLEAN: PILL_COLORS.docNew,
  SYMPTOMS: PILL_COLORS.docReviewed,
  LANGUAGE: PILL_COLORS.docSystemGenerated,
  TITLES: PILL_COLORS.docInProgress,
  DEMOGRAPHICS: PILL_COLORS.docUploaded,
};

const CategoryCell = ({ category }) => {
  if (!category) return "-";

  const colors = CATEGORY_COLORS[category] ?? PILL_COLORS.docUnknown;

  return (
    <Pill color={colors.color} bg={colors.bg} border={colors.border}>
      {toTitleCase(category)}
    </Pill>
  );
};

export const CodeTableTab = ({
  data,
  totalRecords,
  isLoading,
  currentPage,
  rowsPerPage,
  search,
  isSyncing,
  onPageChange,
  onRowsChange,
  onSearchChange,
  onClearSearch,
  onSync,
  onViewCodeTable,
}) => {
  const columns = useMemo(
    () => [
      {
        id: "codeTableId",
        header: "ID",
        width: "100px",
        render: (codeTable) => codeTable.codeTableId || "-",
      },
      {
        id: "description",
        header: "Description",
        width: "minmax(200px, 2fr)",
        getTooltipText: (codeTable) => codeTable.description || "-",
        render: (codeTable) => codeTable.description || "-",
      },
      {
        id: "category",
        header: "Category",
        width: "minmax(140px, 1fr)",
        render: (codeTable) => <CategoryCell category={codeTable.category} />,
      },
      {
        id: "healthlinkLastModified",
        header: "Last Updated",
        width: "minmax(150px, 1fr)",
        render: (codeTable) => codeTable.healthlinkLastModified || "-",
      },
    ],
    []
  );

  const rowActions = useMemo(
    () => [
      {
        label: "View",
        icon: Eye,
        primary: true,
        onClick: (codeTable) => onViewCodeTable(codeTable),
      },
    ],
    [onViewCodeTable]
  );

  return (
    <div className={styles.content}>
      <Notice tone="info" testId="healthlink-code-table-notice">
        <NoticeText>
          View and sync code tables from Healthlink. These tables contain
          medical codes and reference data.
        </NoticeText>
      </Notice>

      <div className={styles.sectionHeader}>
        <h4 className={styles.sectionTitle}>Healthlink Code Tables</h4>
        <Button
          variant="text"
          startIcon={RefreshCw}
          onClick={onSync}
          disabled={isSyncing}
          title="Sync code tables from Healthlink"
        >
          {isSyncing ? "Syncing..." : "Sync Code Tables"}
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={data}
        getRowId={(row) => row.id}
        isLoading={isLoading}
        loadingTitle="Loading code tables..."
        loadingText="Please wait."
        emptyTitle="No code tables found"
        emptyText={
          search
            ? `No code tables match "${search}". Try a different search term.`
            : 'Click "Sync Code Tables" to fetch data from Healthlink.'
        }
        currentPage={currentPage}
        totalCount={totalRecords}
        rowsValue={rowsPerPage}
        onPageChange={onPageChange}
        onRowsChange={onRowsChange}
        search={{
          value: search,
          onChange: onSearchChange,
          placeholder: "Search code tables...",
        }}
        toolbarExtra={
          !!search && (
            <Button variant="outlineGray" size="small" onClick={onClearSearch}>
              Clear
            </Button>
          )
        }
        rowActions={rowActions}
        rowActionsLabel="Code table actions"
      />
    </div>
  );
};
